/**
 * BarChart — Grouped/side-by-side bar chart wrapper
 */

import { lazy, Suspense, useMemo } from "react";
import { CHART_COLORS, getChartTheme, useThemeVersion } from "./chart-theme";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface BarChartSeries {
  name: string;
  data: number[];
  color?: string;
}

interface BarChartProps {
  categories: string[];
  series: BarChartSeries[];
  height?: number;
  yAxisName?: string;
  horizontal?: boolean;
  stacked?: boolean;
}

export function BarChart({
  categories,
  series,
  height = 250,
  yAxisName,
  horizontal = false,
  stacked = false,
}: BarChartProps) {
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const theme = getChartTheme();

    const categoryAxis = {
      type: "category" as const,
      data: categories,
      axisLabel: {
        color: theme.mutedForeground,
        fontSize: 10,
        rotate: !horizontal && categories.length > 8 ? 45 : 0,
      },
      axisLine: { lineStyle: { color: theme.border } },
      axisTick: { show: false },
    };

    const valueAxis = {
      type: "value" as const,
      name: yAxisName,
      nameTextStyle: { color: theme.mutedForeground, fontSize: 10 },
      axisLabel: { color: theme.mutedForeground, fontSize: 10 },
      splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
    };

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis" as const,
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
      },
      legend:
        series.length > 1
          ? {
              data: series.map((s) => s.name),
              textStyle: { color: theme.mutedForeground, fontSize: 11 },
              bottom: 0,
            }
          : undefined,
      grid: {
        left: horizontal ? 60 : 40,
        right: 16,
        top: 16,
        bottom: series.length > 1 ? 36 : 16,
        containLabel: false,
      },
      xAxis: horizontal ? valueAxis : categoryAxis,
      yAxis: horizontal ? categoryAxis : valueAxis,
      series: series.map((s, i) => ({
        name: s.name,
        type: "bar" as const,
        data: s.data,
        color: s.color ?? CHART_COLORS[i % CHART_COLORS.length],
        barMaxWidth: 24,
        stack: stacked ? "total" : undefined,
        itemStyle: { borderRadius: [3, 3, 0, 0] },
        animationDuration: 600,
        animationDelay: i * 100,
      })),
    };
  }, [
    categories,
    series,
    height,
    yAxisName,
    horizontal,
    stacked,
    themeVersion,
  ]);

  return (
    <Suspense fallback={<div style={{ height: `${height}px` }} />}>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Suspense>
  );
}
